import { useState, FC, useEffect } from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Switch,
  message,
  Radio,
  Spin,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../services/react-query/queryKeys";
import {
  addTeamMember,
  editTeamMember,
  showTeamMember,
} from "../../../network/teamMember";
import { MdOutlineDeleteForever } from "react-icons/md";

import PhoneInputWithCountrySelect, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getAllLocations } from "../../../network/location";
import { TeamMemberPermissionEnum } from "../../../enum/teamMemberPermission";
import GenderSwitch from "../../common/GenderSwitch";
import { useIntl } from "react-intl";
import { checkStringInLanguage } from "../../../utils/utils";
import {
  adminAddTeamMember,
  adminDeleteTeamMember,
  adminEditTeamMember,
  adminShowTeamMember,
  getLocationsAdmin,
} from "../../../network/admin";
import ServicesWithCategoriesSelect from "./ServicesWithCategoriesSelect";
import PackagesWithCategoriesSelect from "./PackagesWithCategoriesSelect";
import LocationPermissions from "./LocationPermissions";
import { useSelector } from "react-redux";
import DeleteModal from "../../common/DeleteModal";
import { CiWarning } from "react-icons/ci";
import IMGUploader from "../../common/ui/IMGUploader";

type selectedServicesType = {
  id: number;
  name: string;
  categoryId: number;
};
type selectedPackagesType = {
  id: number;
  name: string;
  retail_price: string;
  total_time: string;
};
interface AddTeamMemberProps {
  visible: boolean;
  showMinForm?: boolean;
  onClose: () => void;
  config: {
    isAdmin?: boolean;
    teamMemberId?: number;
    locationId: number;
    locationName?: string;
    isEdit?: boolean;
    salonName?: string;
    salonId?: number;
  };
}
const AddTeamMember: FC<AddTeamMemberProps> = ({
  visible,
  onClose,
  showMinForm,
  config,
}) => {
  const intl = useIntl();
  const [fileValue, setFileValue] = useState<any>([]);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [selectedServices, setSelectedServices] = useState<
    selectedServicesType[]
  >([]);
  const [selectedPackages, setSelectedPackages] = useState<
    selectedPackagesType[]
  >([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { data: teamMemberDetails, isLoading: isLoadingTeamMember } = useQuery(
    [queryKeys.teamMember, config?.teamMemberId],
    () =>
      config?.isAdmin
        ? adminShowTeamMember(config?.teamMemberId, true)
        : showTeamMember(config?.teamMemberId, true),
    {
      enabled: !!config?.teamMemberId,
      select: (res) => res?.data,
    }
  );
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => state.user
  );
  const initializeTeamMemberData = () => {
    if (teamMemberDetails) {
      const initialData = {
        ...teamMemberDetails,
        services: [{}],
        packages: [{}],
      };
      form.setFieldsValue(initialData);
      const initialServices = teamMemberDetails?.services?.map(
        (service: any) => {
          return {
            id: service?.id,
            name: service?.name[currentLang || "ar"],
            categoryId: service?.service_category_id,
          };
        }
      );
      const initialPackages = teamMemberDetails?.packages?.map(
        (packageItem: any) => {
          return {
            id: packageItem?.id,
            name: packageItem?.name[currentLang || "ar"],
            retail_price: packageItem?.retail_price,
            total_time: packageItem?.total_time,
            categoryId: packageItem?.package_category_id,
          };
        }
      );
      setSelectedServices(() => initialServices);
      setSelectedPackages(() => initialPackages);
      if (teamMemberDetails?.media) {
        setFileValue([
          {
            uid: teamMemberDetails?.media,
            name: teamMemberDetails?.media,
            url: teamMemberDetails?.media,
            size: 100,
          },
        ]);
      }
      let locationsPermissons: any[] = [];
      const teamMemberLocations = teamMemberDetails?.locations?.map(
        (location: any) => {
          locationsPermissons.push({
            location_id: location?.id,
            permission: location?.pivot?.permission,
          });
          return {
            id: location?.id,
            key: location?.id,
          };
        }
      );
      form.setFieldsValue({ location_permissions: locationsPermissons });
      setSelectedLocations(teamMemberLocations);
    }
  };
  useEffect(() => {
    if (teamMemberDetails && config?.teamMemberId) {
      initializeTeamMemberData();
    }
  }, [teamMemberDetails]);
  const { data: listLocations } = useQuery(
    [queryKeys.listLocations, config?.salonId],
    () => {
      if (config?.isAdmin) {
        return getLocationsAdmin({ salon_id: config?.salonId });
      } else {
        return getAllLocations();
      }
    },
    {
      select: (res) => res?.data,
    }
  );
  const [selectedLocations, setSelectedLocations] = useState<
    { id: number; key: number }[]
  >(listLocations?.length ? [{ key: 0, id: listLocations[0]?.id }] : []);
  const { mutate: onAddTeamMember, isLoading } = useMutation(
    (body: object) => {
      return addTeamMember(body);
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST" && res?.response?.status !== 500) {
          message.success("Team member created successfully");
          queryClient.invalidateQueries([queryKeys.listTeamMember]);
          onClose();
        } else {
          message.error(res?.response?.data?.message);
        }
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    }
  );
  const { mutate: onAdminAddTeamMember, isLoading: adminLoading } = useMutation(
    (body: object) => {
      return adminAddTeamMember(body);
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST" && res?.response?.status !== 500) {
          message.success("Team member created successfully");
          queryClient.invalidateQueries(["AdminTeamMembers"]);
          queryClient.invalidateQueries(["location"]);
          onClose();
        } else {
          message.error(res?.response?.data?.message);
        }
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    }
  );
  const { mutate: onEditTeamMember, isLoading: adminEditLoading } = useMutation(
    (body: object) => {
      return config?.isAdmin
        ? adminEditTeamMember(config?.teamMemberId, body)
        : editTeamMember(config?.teamMemberId, body);
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST" && res?.response?.status !== 500) {
          message.success("Team member created successfully");
          queryClient.invalidateQueries([
            config.isAdmin ? "AdminTeamMembers" : queryKeys.listTeamMember,
          ]);
          queryClient.invalidateQueries(["location"]);
          onClose();
        } else {
          message.error(res?.response?.data?.message);
        }
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    }
  );
  const onFinish = async (values: any) => {
    const phoneNumber = parsePhoneNumber(values?.phone || "");
    let form_data = new FormData();
    form_data.append("location_id", String(config?.locationId));
    form_data.append("phone_country_code", phoneNumber?.country || "SA");
    if (values.email) {
      form_data.append("email", values.email);
    }
    form_data.append("title[ar]", values.title?.ar);
    form_data.append("title[en]", values.title?.en);
    form_data.append("name[ar]", values.name?.ar);
    form_data.append("name[en]", values.name?.en);
    form_data.append("phone", phoneNumber?.nationalNumber || "");
    form_data.append("phone_country_code", phoneNumber?.country || "SA");
    form_data.append("available_work_for", values.available_work_for);
    form_data.append("available_for", values.available_for);

    form_data.append("calendar_booking", values?.calendar_booking ? "1" : "0");
    if (values.notes?.ar) {
      form_data.append("notes[ar]", values.notes?.ar);
    }
    values?.location_permissions?.forEach(
      (
        permission: { location_id: any; permission: string | Blob },
        index: any
      ) => {
        if (permission.location_id && permission.permission) {
          form_data.append(
            `location_permissions[${index}][location_id]`,
            String(permission.location_id)
          );
          form_data.append(
            `location_permissions[${index}][permission]`,
            permission.permission
          );
        }
      }
    );
    fileValue[0] &&
      fileValue[0]?.originFileObj &&
      form_data.append("image", fileValue[0]?.originFileObj);
    selectedServices?.forEach((service, index) => {
      form_data.append(`services[${index}][service_id]`, String(service.id));
    });
    selectedPackages?.forEach((pkg, index) => {
      form_data.append(`package_ids[${index}]`, String(pkg.id));
    });
    if (config?.isAdmin === true) {
      if (config?.isEdit) {
        form_data?.delete("phone");
        form_data?.delete("phone_country_code");
        onEditTeamMember(form_data);
        return;
      } else {
        onAdminAddTeamMember(form_data);
      }
    } else {
      if (config?.isEdit) {
        form_data?.delete("phone");
        form_data?.delete("phone_country_code");
        onEditTeamMember(form_data);
        return;
      }
      onAddTeamMember(form_data);
    }
  };
  const [active, setActive] = useState();
  const [active2, setActive2] = useState();
  const changeGender = (e: any) => {
    setActive(e.target.value);
  };
  const changeGender2 = (e: any) => {
    setActive2(e.target.value);
  };
  return (
    <Modal
      centered
      open={visible}
      onCancel={onClose}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={null}
      width={1060}
      maskClosable={false}
      // closable={false}
      destroyOnClose={true}
    >
      <div className="text-color-primary text-2xl font-semibold leading-7 text-center">
        {intl.formatMessage({
          id: config?.isEdit ? "updateTeamMember" : "addTeamMember",
          defaultMessage: "اضافة عضو جديد للفريق",
        })}
      </div>
      <div className="my-4 text-color-gray text-base font-normal leading-7 text-center">
        {config?.isEdit
          ? config?.salonName
          : intl.formatMessage({
              id: "completeTeamMemberInfo",
              defaultMessage: "أكمل المعلومات الآتية لاضافة عضو جديد لفريقك",
            })}
      </div>
      {isLoadingTeamMember && config?.teamMemberId ? (
        <div className="w-full h-full flex justify-center items-center">
          <Spin size="large" />
        </div>
      ) : (
        <Form
          name="complex-form"
          onFinish={onFinish}
          layout="vertical"
          className="form-style"
          form={form}
          initialValues={{
            services: [{}],
            packages: [{}],
            location_permissions: [
              {
                location_id: config?.locationId,
                permission: TeamMemberPermissionEnum.NoAccess, // Set the initial value to NoAccess
              },
            ],
            enable_online_booking: false,
            calendar_booking: true,
          }}
        >
          {!showMinForm ? (
            <IMGUploader
              name="image"
              imgValidation
              fileList={fileValue}
              setFileList={setFileValue}
              listType="picture-circle"
            />
          ) : null}

          <Row gutter={{ xs: 1, sm: 1, md: 8, lg: 14, xl: 14 }}>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name={["name", "ar"]}
                label={intl.formatMessage({
                  id: "memberNameAr",
                  defaultMessage: "اسم العضو",
                })}
                required={false}
                className="form-style w-full"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "memberNameRequired",
                      defaultMessage: "اسم العضو مطلوب",
                    }),
                  },
                  {
                    type: "string",
                    min: 2,
                    message: intl.formatMessage({
                      id: "memberNameRequired2Chars",
                      defaultMessage:
                        "يجب أن يحتوي الاسم على ما لا يقل عن 2 أحرف",
                    }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "ar")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_arabic_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: "enterMemberName",
                    defaultMessage: "ادخل اسم العضو",
                  })}
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name={["name", "en"]}
                label={intl.formatMessage({
                  id: "memberNameEn",
                })}
                required={false}
                className="form-style w-full"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "memberNameRequired",
                      defaultMessage: "اسم العضو مطلوب",
                    }),
                  },
                  {
                    type: "string",
                    min: 2,
                    message: intl.formatMessage({
                      id: "memberNameRequired2Chars",
                      defaultMessage:
                        "يجب أن يحتوي الاسم على ما لا يقل عن 2 أحرف",
                    }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "en")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_english_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: "enterMemberName",
                    defaultMessage: "ادخل اسم العضو",
                  })}
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name={["title", "ar"]}
                label={intl.formatMessage({
                  id: "memberJobAr",
                  defaultMessage: "وظيفة العضو",
                })}
                className="w-full"
                required={false}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "memberJobRequired",
                      defaultMessage: "وظيفة العضو مطلوبة",
                    }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "ar")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_arabic_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: "enterMemberJob",
                    defaultMessage: "ادخل وظيفة العضو ",
                  })}
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name={["title", "en"]}
                label={intl.formatMessage({
                  id: "memberJobEn",
                })}
                className="w-full"
                required={false}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "memberJobRequired",
                      defaultMessage: "وظيفة العضو مطلوبة",
                    }),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        if (checkStringInLanguage(value, "en")) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "service_name_english_error",
                              })
                            )
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: "enterMemberJob",
                    defaultMessage: "ادخل وظيفة العضو ",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14, xl: 14 }}>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name="email"
                label={
                  <p>
                    {intl.formatMessage({
                      id: "email",
                      defaultMessage: "البريد الإلكتروني",
                    })}
                    <span className="text-sm text-gray-500">
                      {" "}
                      {"( "}{" "}
                      {intl.formatMessage({
                        id: "optional",
                      })}{" "}
                      {" )"}
                    </span>
                  </p>
                }
                required={false}
                className="form-style w-full"
                rules={[
                  {
                    type: "email",
                    message: intl.formatMessage({
                      id: "invalidEmail",
                      defaultMessage: "البريد الإلكتروني غير صالح",
                    }),
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: "enterEmail",
                    defaultMessage: "ادخل البريد الإلكتروني",
                  })}
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name="phone"
                label={intl.formatMessage({
                  id: "phoneNumber",
                  defaultMessage: "رقم جوال ",
                })}
                required={false}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "phoneRequired",
                      defaultMessage: "رقم جوال مطلوب",
                    }),
                  },
                  {
                    validator: (rule, value) => {
                      if (isValidPhoneNumber(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        intl.formatMessage({
                          id: "phoneInvalid",
                          defaultMessage: "رقم الجوال غير صالح",
                        })
                      );
                    },
                  },
                ]}
                className="w-full"
                hasFeedback
              >
                <PhoneInputWithCountrySelect
                  defaultCountry="SA"
                  className="w-full"
                  onChange={(value) => {}}
                  name="phone_country_code"
                  international
                  hasFeedback
                  limitMaxLength={true}
                  disabled={config?.isEdit}
                />
              </Form.Item>
              <div className="flex items-center gap-1 mb-4 -mt-4">
                <CiWarning size={40} color="#FF0000" />

                <p className="text-color-sec-title">
                  {intl.formatMessage({ id: "tm-phone-warning" })}
                </p>
              </div>
            </Col>
          </Row>
          <LocationPermissions
            selectedLocations={selectedLocations}
            setSelectedLocations={setSelectedLocations}
            showMinForm={showMinForm}
            config={config}
            listLocations={listLocations}
          />

          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            justify={"space-between"}
          >
            <Col className="gutter-row">
              <label className="text-right font-Cairo text-base font-normal mt-3 w-full text-xl leading-6 tracking-wider ">
                {intl.formatMessage({
                  id: "gender",
                  defaultMessage: "الجنس",
                })}
              </label>
            </Col>
            <Col className="gutter-row">
              <Form.Item
                name="available_for"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "selectGender",
                      defaultMessage: "اختر الجنس ",
                    }),
                  },
                ]}
              >
                <Radio.Group onChange={changeGender2}>
                  <Row
                    gutter={{ xs: 8, sm: 0, md: 24, lg: 14 }}
                    justify={"space-between"}
                  >
                    <Col className="gutter-row">
                      <GenderSwitch
                        active={"woman" === active2}
                        gender="woman"
                        string={intl.formatMessage({
                          id: "womanGender",
                          defaultMessage: "إمرأة",
                        })}
                      />
                    </Col>
                    <Col className="gutter-row">
                      <GenderSwitch
                        active={"man" === active2}
                        gender="man"
                        string={intl.formatMessage({
                          id: "manGender",
                          defaultMessage: "رجل",
                        })}
                      />
                    </Col>
                  </Row>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            justify={"space-between"}
          >
            <Col className="gutter-row">
              <label className="text-right font-Cairo font-normal mt-3 w-full text-xl leading-6 tracking-wider ">
                {intl.formatMessage({
                  id: "workWithAbility",
                  defaultMessage: "قابلية العمل مع",
                })}
              </label>
            </Col>
            <Col className="gutter-row max-md:my-1 ">
              <Form.Item
                name="available_work_for"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "selectGender",
                      defaultMessage: "اختر الجنس ",
                    }),
                  },
                ]}
              >
                <Radio.Group onChange={changeGender}>
                  <Row
                    gutter={{ xs: 8, sm: 0, md: 24, lg: 14 }}
                    justify={"space-between"}
                  >
                    <Col className="gutter-row">
                      <GenderSwitch
                        active={"woman" === active}
                        gender="woman"
                        string={intl.formatMessage({
                          id: "onlyWomen",
                          defaultMessage: "النساء فقط",
                        })}
                      />
                    </Col>
                    <Col className="gutter-row">
                      <GenderSwitch
                        active={"man" === active}
                        gender="man"
                        string={intl.formatMessage({
                          id: "onlyMen",
                          defaultMessage: "الرجال فقط",
                        })}
                      />
                    </Col>
                  </Row>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {!showMinForm && (
            <>
              <ServicesWithCategoriesSelect
                form={form}
                selectedLocations={selectedLocations}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
                config={config}
              />
              <PackagesWithCategoriesSelect
                form={form}
                selectedLocations={selectedLocations}
                selectedPackages={selectedPackages}
                setSelectedPackages={setSelectedPackages}
                config={config}
              />

              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  xs={24}
                  sm={24}
                  span={11}
                >
                  <label className="text-base font-semibold mt-3 w-full">
                    {intl.formatMessage({
                      id: "bookingWithMember",
                      defaultMessage: "الحجز مع العضو",
                    })}
                  </label>
                </Col>
                <Col
                  className="gutter-row flex justify-end"
                  xl={12}
                  lg={12}
                  xs={24}
                  sm={24}
                  span={11}
                >
                  <Form.Item
                    name="calendar_booking"
                    className="float-left"
                    rules={[{ required: true, message: "صفقة جديدة مطلوب" }]}
                  >
                    <Switch defaultChecked />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name={["notes", "ar"]}
                label={
                  <p>
                    {intl.formatMessage({
                      id: "notes",
                      defaultMessage: "ملاحظات",
                    })}
                    <span className="text-sm text-gray-500">
                      {" "}
                      {"( "}{" "}
                      {intl.formatMessage({
                        id: "optional",
                      })}{" "}
                      {" )"}
                    </span>
                  </p>
                }
                required={false}
                className="w-full"
              >
                <Input.TextArea
                  size="large"
                  rows={2}
                  placeholder={intl.formatMessage({
                    id: "enterNotes",
                    defaultMessage: "أدخل ملاحظات",
                  })}
                />
              </Form.Item>
            </>
          )}
          <Flex gap="middle" align="center" justify="center">
            <Button
              type="primary"
              block
              htmlType="submit"
              loading={isLoading || adminLoading || adminEditLoading}
            >
              {config?.isEdit
                ? "Save changes"
                : intl.formatMessage({
                    id: "addMember",
                    defaultMessage: "إضافة العضو",
                  })}
            </Button>
            <Button
              type="primary"
              block
              className="btn-primary-light"
              loading={isLoading || adminLoading || adminEditLoading}
              onClick={() => {
                form.resetFields();
                onClose();
              }}
            >
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "إلغاء",
              })}
            </Button>
          </Flex>
          {config?.isEdit && config?.isAdmin && config?.teamMemberId ? (
            <div className="text-[#C43535] text-base font-medium flex items-center justify-center mt-8">
              <DeleteModal
                visible={openDeleteModal}
                onClose={() => {
                  setOpenDeleteModal(false);
                }}
                onDelete={adminDeleteTeamMember} // Pass the delete function for "Package"
                data={config?.teamMemberId}
                confirmationHeader={
                  "Are you sure you want to delete this team member?"
                }
                confirmationText={""}
                queryKey={config?.isAdmin ? "AdminTeamMembers" : "location"} // Pass the correct key here
                successMessage={"Team member deleted successfully"}
                afterDelete={onClose}
              />
              <Button
                type="text"
                danger
                block
                htmlType="button"
                icon={<MdOutlineDeleteForever className="text-lg" />}
                // className="bg-white py-3 min-w-[150px] rounded-r-lg text-[#00086D] font-semibold"
                onClick={() => {
                  setOpenDeleteModal(true);
                }}
              >
                Delete team member
              </Button>
            </div>
          ) : null}
        </Form>
      )}
    </Modal>
  );
};
export default AddTeamMember;
